import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring, animate } from "framer-motion";
import { MessageCircle, Star, PawPrint } from "lucide-react";
import { useTranslation } from "react-i18next";
import { localePath } from "@/i18n/localePath";
import heroDog from "@/assets/hero-dog-clean.png";

/* ─── Animated counter ─── */
const AnimatedCounter: React.FC<{ target: number; suffix?: string; delay?: number }> = ({ target, suffix = "", delay = 0 }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      const controls = animate(0, target, {
        duration: 2,
        ease: [0.25, 0.1, 0.25, 1],
        onUpdate: (v) => setValue(Math.floor(v)),
      });
      return () => controls.stop();
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);
  return <>{value.toLocaleString()}{suffix}</>;
};

/* ─── Floating chat bubble decoration ─── */
const FloatingChatBubble: React.FC<{
  text: string;
  side: "left" | "right";
  delay: number;
  x: string;
  y: string;
  rotate?: number;
}> = ({ text, side, delay, x, y, rotate = 0 }) => (
  <motion.div
    className={`absolute z-20 max-w-[180px] ${
      side === "right"
        ? "rounded-[16px] rounded-br-[4px] bg-primary text-primary-foreground"
        : "rounded-[16px] rounded-bl-[4px] glass"
    } px-3.5 py-2.5 shadow-warm-md`}
    style={{ left: x, top: y, rotate }}
    initial={{ opacity: 0, scale: 0.7, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
  >
    <p className={`text-xs leading-snug ${side === "left" ? "text-foreground" : ""}`}>{text}</p>
  </motion.div>
);

/* ─── Sparkle ─── */
const Sparkle: React.FC<{ delay: number; x: string; y: string; size?: number }> = ({ delay, x, y, size = 4 }) => (
  <motion.div
    className="absolute rounded-full bg-primary/50 pointer-events-none"
    style={{ left: x, top: y, width: size, height: size }}
    animate={{ scale: [0, 1, 0], opacity: [0, 0.8, 0] }}
    transition={{ duration: 2.5, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);

/* ─── Main Hero ─── */
export const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lp = (path: string) => localePath(path, i18n.language);
  const sectionRef = useRef<HTMLElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const titleY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const fadeOut = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      const cx = (e.clientX / window.innerWidth - 0.5) * 2;
      const cy = (e.clientY / window.innerHeight - 0.5) * 2;
      mouseX.set(cx * 12);
      mouseY.set(cy * 8);
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, [mouseX, mouseY]);

  const scrollToHowItWorks = () => {
    document.querySelector("#how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-[88dvh] flex items-center px-4 sm:px-6 overflow-hidden"
    >
      {/* ─── Background ─── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-warm-peach/15 to-background" />
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 600, height: 600, right: "-5%", top: "5%",
            background: "radial-gradient(circle, hsl(20 100% 65% / 0.12) 0%, hsl(35 80% 70% / 0.06) 50%, transparent 70%)",
            filter: "blur(60px)",
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 400, height: 400, left: "5%", bottom: "10%",
            background: "radial-gradient(circle, hsl(35 80% 65% / 0.08) 0%, transparent 65%)",
            filter: "blur(80px)",
          }}
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Sparkles */}
      {[
        { x: "15%", y: "20%", d: 0, s: 3 }, { x: "80%", y: "15%", d: 1.2, s: 5 },
        { x: "25%", y: "70%", d: 2.5, s: 4 }, { x: "70%", y: "75%", d: 0.8, s: 3 },
        { x: "50%", y: "30%", d: 3.5, s: 4 }, { x: "90%", y: "50%", d: 1.8, s: 3 },
        { x: "10%", y: "45%", d: 4, s: 5 }, { x: "60%", y: "85%", d: 2, s: 3 },
      ].map((s, i) => (
        <Sparkle key={i} delay={s.d} x={s.x} y={s.y} size={s.s} />
      ))}

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/80 to-transparent z-[2]" />

      {/* ─── Two-column content ─── */}
      <motion.div
        className="max-w-6xl w-full mx-auto flex flex-col lg:flex-row items-center gap-6 lg:gap-8 relative z-10 py-12 sm:py-16"
        style={{ opacity: fadeOut }}
      >
        {/* Left: Text */}
        <motion.div className="flex-1 text-center lg:text-left" style={{ y: titleY }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/15 mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-semibold text-primary">{t('hero.badge')}</span>
          </motion.div>

          <motion.div style={{ x: smoothX, y: smoothY }}>
            <motion.h1
              initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-[40px] sm:text-[52px] md:text-[64px] lg:text-[72px] font-[800] tracking-[3px] sm:tracking-[5px] leading-[0.95] text-foreground"
            >
              {"PETERNITY".split("").map((char, i) => (
                <motion.span
                  key={i}
                  className="inline-block"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.05, ease: [0.25, 0.1, 0.25, 1] }}
                  whileHover={{ y: -8, color: "hsl(var(--primary))", transition: { duration: 0.2 } }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="text-lg sm:text-xl font-medium text-foreground/70 mt-4 leading-snug"
          >
            {t('hero.tagline_main')}{" "}
            <span className="text-primary">{t('hero.tagline_highlight')}</span>
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="text-sm text-muted-foreground mt-3 max-w-md mx-auto lg:mx-0 leading-relaxed"
          >
            {t('hero.value_prop')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.6 }}
            className="mt-8 flex flex-col sm:flex-row items-center lg:items-start gap-3"
          >
            <motion.button
              onClick={() => navigate(lp("/auth"))}
              className="relative group px-10 py-4 rounded-2xl font-bold text-primary-foreground overflow-hidden text-base"
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))",
                boxShadow: "0 8px 32px hsl(var(--primary) / 0.3), 0 0 60px hsl(var(--primary) / 0.1)",
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: "linear-gradient(105deg, transparent 20%, hsla(0,0%,100%,0.2) 45%, hsla(0,0%,100%,0.3) 50%, hsla(0,0%,100%,0.2) 55%, transparent 80%)",
                  backgroundSize: "200% 100%",
                }}
                animate={{ backgroundPosition: ["-100% 0", "200% 0"] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
              />
              <span className="relative z-10 flex items-center gap-2">
                {t('hero.cta_primary')}
              </span>
            </motion.button>

            <button
              onClick={scrollToHowItWorks}
              className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 py-2"
            >
              {t('hero.cta_secondary')}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.0, duration: 0.8 }}
            className="mt-10 flex items-center gap-6 sm:gap-8 justify-center lg:justify-start"
          >
            {[
              { icon: <PawPrint className="w-4 h-4 text-primary/60" />, value: 2400, suffix: "+", label: t('hero.stats.pets_label'), delay: 2000 },
              { icon: <Star className="w-4 h-4 text-primary/60" />, value: 98, suffix: "%", label: t('hero.stats.happy_label'), delay: 2200 },
              { icon: <MessageCircle className="w-4 h-4 text-primary/60" />, value: 500, suffix: "K", label: t('hero.stats.messages_label'), delay: 2400 },
            ].map((stat, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="w-px h-10 bg-border/50" />}
                <div className="flex flex-col items-center gap-0.5">
                  <div className="flex items-center gap-1.5">
                    {stat.icon}
                    <span className="text-xl font-bold text-foreground/80">
                      <AnimatedCounter target={stat.value} suffix={stat.suffix} delay={stat.delay} />
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground/50">{stat.label}</span>
                </div>
              </React.Fragment>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: Dog with floating bubbles */}
        <motion.div className="flex-1 flex justify-center relative" style={{ y: imageY }}>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
              className="w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] lg:w-[400px] lg:h-[400px] rounded-full"
              style={{
                background: "radial-gradient(circle, hsl(var(--primary) / 0.1) 0%, hsl(var(--primary) / 0.04) 50%, transparent 70%)",
              }}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          <FloatingChatBubble text={t('hero_bubbles.bubble_1')} side="left" delay={1.8} x="-10%" y="15%" rotate={-3} />
          <FloatingChatBubble text={t('hero_bubbles.bubble_2')} side="right" delay={2.2} x="65%" y="5%" rotate={2} />
          <FloatingChatBubble text={t('hero_bubbles.bubble_3')} side="left" delay={2.6} x="60%" y="70%" rotate={1} />

          <motion.img
            src={heroDog}
            alt={t('hero.dog_alt')}
            className="relative z-10 w-[260px] sm:w-[300px] lg:w-[380px] object-contain"
            loading="eager"
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ filter: "drop-shadow(0 20px 40px hsl(20 60% 40% / 0.15))" }}
          />

          {[
            { x: "5%", y: "60%", d: 3, r: -20 },
            { x: "80%", y: "45%", d: 4.5, r: 15 },
          ].map((p, i) => (
            <motion.span
              key={i}
              className="absolute text-lg pointer-events-none select-none"
              style={{ left: p.x, top: p.y, rotate: p.r, opacity: 0.12 }}
              animate={{ y: [0, -10, 0], opacity: [0.08, 0.16, 0.08] }}
              transition={{ duration: 5, delay: p.d, repeat: Infinity, ease: "easeInOut" }}
            >
              🐾
            </motion.span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        style={{ opacity: fadeOut }}
      >
        <div className="w-5 h-8 rounded-full border-2 border-muted-foreground/15 flex justify-center pt-1.5">
          <motion.div
            animate={{ y: [0, 10, 0], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-1 rounded-full bg-primary/50"
          />
        </div>
      </motion.div>
    </section>
  );
};
